import * as React from "react";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import styled from "styled-components";
import trim from "lodash/trim";
import { Field, useFormikContext } from "formik";
import i18n from "i18n";
import { Input } from "components/bootstrap";
import { ColorPickerPopover, Select } from "components/common";
import { DEFAULT_CUSTOM_HIGHLIGHT_RANGE } from "views/Constants";
import ColorPreview, { GradientColorPreview } from "views/components/sidebar/highlighting/ColorPreview";
import { StaticColor } from "views/logic/views/formatting/highlighting/HighlightingColor";
import { COLORSCALES } from "views/logic/aggregationbuilder/visualizations/HeatmapVisualizationConfig";
import { defaultCompare } from "logic/DefaultCompare";
const StaticColorPicker = () => {
  const { t } = useTranslation("search");
  return /* @__PURE__ */ React.createElement(Field, { name: "color.color" }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(Input, { id: name, error: meta == null ? void 0 : meta.error, label: t("highlighting_color.color") }, /* @__PURE__ */ React.createElement(
    ColorPickerPopover,
    {
      id: "formatting-rule-color",
      placement: "right",
      color: value,
      colors: DEFAULT_CUSTOM_HIGHLIGHT_RANGE.map((c) => [c]),
      triggerNode: /* @__PURE__ */ React.createElement(ColorPreview, { color: StaticColor.create(value) }),
      onChange: (newColor, _, hidePopover) => {
        hidePopover();
        onChange({ target: { name, value: newColor } });
      }
    }
  )));
};
const OptionContainer = styled.div`
  display: flex;
`;
const createOption = (name) => ({
  label: /* @__PURE__ */ React.createElement(OptionContainer, null, /* @__PURE__ */ React.createElement(GradientColorPreview, { $gradient: name }), name),
  value: name
});
const GRADIENTS = [...COLORSCALES].sort(defaultCompare).map(createOption);
const numericRegex = /^-?[0-9]+$/;
const validateColor = (values) => {
  const errors = {};
  if ((values == null ? void 0 : values.type) === "gradient") {
    if (trim(values == null ? void 0 : values.gradient) === "") {
      errors.gradient = i18n.t("highlighting_color.must_be_selected", { ns: "search" });
    }
    if (trim(values == null ? void 0 : values.lower) === "") {
      errors.lower = i18n.t("highlighting_color.must_be_present", { ns: "search" });
    } else if (!numericRegex.test(values == null ? void 0 : values.lower)) {
      errors.lower = i18n.t("highlighting_color.must_be_number", { ns: "search" });
    }
    if (trim(values == null ? void 0 : values.upper) === "") {
      errors.upper = i18n.t("highlighting_color.must_be_present", { ns: "search" });
    } else if (!numericRegex.test(values == null ? void 0 : values.upper)) {
      errors.upper = i18n.t("highlighting_color.must_be_number", { ns: "search" });
    }
    if ((values == null ? void 0 : values.upper) <= (values == null ? void 0 : values.lower)) {
      errors.upper = i18n.t("highlighting_color.must_be_higher", { ns: "search" });
    }
  }
  return Object.keys(errors).length > 0 ? errors : void 0;
};
const GradientColorPicker = () => {
  const { t } = useTranslation("search");
  return /* @__PURE__ */ React.createElement(Field, { name: "color", validate: validateColor }, () => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Field, { name: "color.gradient" }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(Input, { id: `${name}-name`, error: meta == null ? void 0 : meta.error, label: t("highlighting_color.gradient_name") }, /* @__PURE__ */ React.createElement(
    Select,
    {
      options: GRADIENTS,
      placeholder: t("highlighting_color.gradient_placeholder"),
      value,
      onChange: (newGradient) => onChange({ target: { name, value: newGradient } })
    }
  ))), /* @__PURE__ */ React.createElement(Field, { name: "color.lower" }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: name,
      placeholder: t("highlighting_color.lowest_placeholder"),
      label: t("highlighting_color.lowest_label"),
      type: "number",
      value,
      error: meta == null ? void 0 : meta.error,
      onChange,
      help: t("highlighting_color.lowest_help"),
      required: true
    }
  )), /* @__PURE__ */ React.createElement(Field, { name: "color.upper" }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: name,
      "aria-label": t("highlighting_color.highest_aria"),
      label: t("highlighting_color.highest_label"),
      type: "number",
      value,
      error: meta == null ? void 0 : meta.error,
      onChange,
      help: t("highlighting_color.highest_help"),
      required: true
    }
  ))));
};
const ColorForm = ({ type }) => {
  switch (type) {
    case "static":
      return /* @__PURE__ */ React.createElement(StaticColorPicker, null);
    case "gradient":
      return /* @__PURE__ */ React.createElement(GradientColorPicker, null);
    default:
      return null;
  }
};
const Container = styled.div`
  margin-left: 10px;
`;
const randomColor = () => DEFAULT_CUSTOM_HIGHLIGHT_RANGE[Math.floor(Math.random() * DEFAULT_CUSTOM_HIGHLIGHT_RANGE.length)];
export const createNewColor = (newType) => {
  if (newType === "static") {
    return {
      type: "static",
      color: randomColor()
    };
  }
  if (newType === "gradient") {
    return {
      type: "gradient",
      gradient: "Viridis",
      upper: 0,
      lower: 0
    };
  }
  throw new Error(`Invalid color type: ${newType}`);
};
export const validateColoringType = (value, fieldIsNumeric) => {
  if (!value || value === "") {
    return i18n.t("highlighting_color.coloring_required", { ns: "search" });
  }
  if (!fieldIsNumeric && value === "gradient") {
    return i18n.t("highlighting_color.gradient_numeric_only", { ns: "search" });
  }
  return void 0;
};
const HighlightingColorForm = ({ field }) => {
  var _a, _b;
  const { t } = useTranslation("search");
  const isNumeric = (_b = (_a = field == null ? void 0 : field.type) == null ? void 0 : _a.isNumeric()) != null ? _b : false;
  const { setFieldValue } = useFormikContext();
  const onChangeType = useCallback(
    (e) => {
      const {
        target: { value: newType }
      } = e;
      setFieldValue("color", createNewColor(newType));
    },
    [setFieldValue]
  );
  return /* @__PURE__ */ React.createElement(Field, { name: "color.type", validate: (value) => validateColoringType(value, isNumeric) }, ({ field: { name, value }, meta }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Input, { id: `${name}-coloring`, label: t("highlighting_color.coloring"), error: meta == null ? void 0 : meta.error }, /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      checked: value === "static",
      formGroupClassName: "",
      id: name,
      label: t("highlighting_color.static_color"),
      onChange: onChangeType,
      type: "radio",
      value: "static"
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      checked: value === "gradient",
      formGroupClassName: "",
      id: name,
      disabled: !isNumeric,
      label: t("highlighting_color.gradient"),
      onChange: onChangeType,
      type: "radio",
      value: "gradient"
    }
  ))), value && /* @__PURE__ */ React.createElement(ColorForm, { type: value })));
};
export default HighlightingColorForm;
